import { useId, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { initials } from './text.js'

export interface RecipientContact {
  name: string
  email: string
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SEPARATOR = /[,;\s]/

/** Commas, semicolons or spaces. A pasted list from a mail client works. */
export function parseAddressList(value: string): string[] {
  const seen = new Set<string>()
  const addresses: string[] = []
  for (const part of value.split(/[,;\s]+/)) {
    const address = part.trim().replace(/^<|>$/g, '')
    const key = address.toLowerCase()
    if (!address || seen.has(key)) continue
    seen.add(key)
    addresses.push(address)
  }
  return addresses
}

export function isEmail(address: string): boolean {
  return EMAIL.test(address)
}

function merge(current: string[], added: string[]): string[] {
  const seen = new Set(current.map((address) => address.toLowerCase()))
  const next = [...current]
  for (const address of added) {
    if (seen.has(address.toLowerCase())) continue
    seen.add(address.toLowerCase())
    next.push(address)
  }
  return next
}

/**
 * A row of people. What is typed becomes a chip at a comma, a space, Enter,
 * Tab or on leaving the box, and a pasted list becomes several; Backspace in
 * an empty box takes the last chip back. The job's contacts are offered as
 * you type, by name or by address. Text not yet turned into a chip is the
 * caller's (`text`), so pressing Send with an address half-typed still sends
 * to it.
 */
export function RecipientField({
  id,
  label,
  value,
  text,
  contacts,
  placeholder,
  trailing,
  onChange,
  onText,
}: {
  id: string
  label: string
  value: string[]
  text: string
  contacts: RecipientContact[]
  placeholder?: string
  trailing?: ReactNode
  onChange: (value: string[]) => void
  onText: (text: string) => void
}) {
  const listId = useId()
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(0)

  const byAddress = useMemo(() => {
    const map = new Map<string, string>()
    for (const person of contacts) map.set(person.email.toLowerCase(), person.name)
    return map
  }, [contacts])

  const term = text.trim().toLowerCase()
  const offered = focused
    ? contacts
        .filter((person) => !value.some((address) => address.toLowerCase() === person.email.toLowerCase()))
        .filter(
          (person) => term === '' || person.name.toLowerCase().includes(term) || person.email.toLowerCase().includes(term),
        )
        .slice(0, 6)
    : []
  const open = offered.length > 0 && (term !== '' || value.length === 0)

  const commit = (raw = text) => {
    const added = parseAddressList(raw)
    if (added.length) onChange(merge(value, added))
    onText('')
    setActive(0)
  }

  const pick = (person: RecipientContact) => {
    onChange(merge(value, [person.email]))
    onText('')
    setActive(0)
  }

  const remove = (address: string) => onChange(value.filter((item) => item !== address))

  return (
    <div className="flex min-h-11 items-start gap-3 border-b border-ink-100 py-1.5">
      <label htmlFor={id} className="w-14 shrink-0 pt-1.5 text-[13px] text-ink-500">
        {label}
      </label>
      <div className="relative flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {value.length > 0 ? (
          <ul aria-label={`${label} recipients`} className="contents">
            {value.map((address) => {
              const name = byAddress.get(address.toLowerCase())
              const valid = isEmail(address)
              return (
                <li
                  key={address}
                  title={valid ? undefined : 'Not an email address'}
                  className={cn(
                    'group flex max-w-full items-center gap-1.5 rounded-md py-0.5 pr-1.5 pl-0.5 text-[13px]',
                    valid ? 'bg-ink-50 ring-1 ring-ink-200 ring-inset' : 'bg-red-50 ring-1 ring-red-300 ring-inset',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="grid size-6 shrink-0 place-items-center rounded-sm bg-ink-200 text-[10px] font-semibold text-ink-700"
                  >
                    {initials(name ?? address)}
                  </span>
                  {name ? <span className="truncate font-semibold text-ink-900">{name}</span> : null}
                  <span className={cn('truncate', name ? 'text-ink-500' : valid ? 'text-ink-800' : 'text-red-700')}>
                    {address}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${address}`}
                    className="grid size-5 place-items-center rounded-sm text-ink-400 hover:bg-ink-200 hover:text-ink-900"
                    onClick={() => remove(address)}
                  >
                    <Icon name="close" className="size-3" />
                  </button>
                </li>
              )
            })}
          </ul>
        ) : null}
        <input
          id={id}
          aria-label={label}
          value={text}
          inputMode="email"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          placeholder={value.length === 0 ? placeholder : undefined}
          className="min-w-32 flex-1 border-0 bg-transparent py-1 text-[14px] text-ink-900 outline-none placeholder:text-ink-400"
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            if (text.trim()) commit()
          }}
          onChange={(event) => {
            const next = event.target.value
            // A separator ends an address; a pasted list arrives in one change.
            if (SEPARATOR.test(next) && next.trim() !== '') {
              const last = next.split(/[,;\s]+/).at(-1) ?? ''
              const complete = SEPARATOR.test(next.at(-1) ?? '') ? next : next.slice(0, next.length - last.length)
              commit(complete)
              onText(SEPARATOR.test(next.at(-1) ?? '') ? '' : last)
              return
            }
            onText(next)
            setActive(0)
          }}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData('text')
            if (!SEPARATOR.test(pasted.trim())) return
            event.preventDefault()
            commit(`${text} ${pasted}`)
          }}
          onKeyDown={(event) => {
            if (open && event.key === 'ArrowDown') {
              event.preventDefault()
              setActive((index) => Math.min(index + 1, offered.length - 1))
            } else if (open && event.key === 'ArrowUp') {
              event.preventDefault()
              setActive((index) => Math.max(index - 1, 0))
            } else if (event.key === 'Enter') {
              if (open && offered[active]) {
                event.preventDefault()
                pick(offered[active])
              } else if (text.trim()) {
                event.preventDefault()
                commit()
              }
            } else if (event.key === 'Tab' && text.trim()) {
              commit()
            } else if (event.key === 'Backspace' && text === '' && value.length > 0) {
              onChange(value.slice(0, -1))
            }
          }}
        />
        {open ? (
          <ul
            id={listId}
            role="listbox"
            aria-label={`People for ${label}`}
            className="absolute top-full left-0 z-20 mt-1 w-full max-w-sm overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-ink-200"
          >
            {offered.map((person, index) => (
              <li
                key={person.email}
                role="option"
                aria-selected={index === active}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px]',
                  index === active ? 'bg-ink-50' : 'hover:bg-ink-50',
                )}
                onMouseDown={(event) => {
                  event.preventDefault()
                  pick(person)
                }}
              >
                <span
                  aria-hidden="true"
                  className="grid size-6 shrink-0 place-items-center rounded-sm bg-ink-200 text-[10px] font-semibold text-ink-700"
                >
                  {initials(person.name)}
                </span>
                <span className="font-medium text-ink-900">{person.name}</span>
                <span className="truncate text-ink-500">{person.email}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-2 pt-1.5">{trailing}</div> : null}
    </div>
  )
}
